import type { TextDiffOptions, TextDiffResult } from './textDiff'

export const TEXT_DIFF_DEADLINE_MS = 1_500

interface TextDiffWorkerResponse {
  requestId: number
  outcome: TextDiffWorkerOutcome
}

export type TextDiffWorkerOutcome =
  | { type: 'success', result: TextDiffResult }
  | { type: 'timeout' }
  | { type: 'processing_failed' }

export interface TextDiffWorkerRequest {
  promise: Promise<TextDiffWorkerOutcome>
  cancel: () => void
}

interface WorkerLike {
  onmessage: ((event: MessageEvent<TextDiffWorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => void) | null
  postMessage: (message: unknown) => void
  terminate: () => void
}

type WorkerFactory = () => WorkerLike
type SetTimer = (callback: () => void, delay: number) => ReturnType<typeof setTimeout>
type ClearTimer = (handle: ReturnType<typeof setTimeout>) => void

function createWorker(): WorkerLike {
  return new Worker(new URL('./textDiff.worker.ts', import.meta.url), { type: 'module' })
}

/**
 * Runs the complete diff pipeline outside the UI thread. The deadline starts
 * before worker creation, so queueing, computation and result delivery share
 * one bounded budget.
 */
export function runTextDiffInWorker(
  original: string,
  modified: string,
  options: TextDiffOptions,
  deadlineMs = TEXT_DIFF_DEADLINE_MS,
  workerFactory: WorkerFactory = createWorker,
  setTimer: SetTimer = window.setTimeout,
  clearTimer: ClearTimer = window.clearTimeout,
): TextDiffWorkerRequest {
  const requestId = 1
  let settled = false
  let resolvePromise: (outcome: TextDiffWorkerOutcome) => void = () => undefined

  const promise = new Promise<TextDiffWorkerOutcome>((resolve) => {
    resolvePromise = resolve
  })
  let worker: WorkerLike
  try {
    worker = workerFactory()
  } catch {
    resolvePromise({ type: 'processing_failed' })
    return { promise, cancel: () => undefined }
  }

  const deadlineAt = Date.now() + deadlineMs
  const timer = setTimer(() => finish({ type: 'timeout' }), deadlineMs)

  function finish(outcome: TextDiffWorkerOutcome): void {
    if (settled) return
    settled = true
    clearTimer(timer)
    worker.terminate()
    resolvePromise(outcome)
  }

  worker.onmessage = (event) => {
    if (event.data.requestId === requestId) finish(event.data.outcome)
  }
  worker.onerror = () => finish({ type: 'processing_failed' })
  try {
    worker.postMessage({ requestId, original, modified, options, deadlineAt })
  } catch {
    finish({ type: 'processing_failed' })
  }

  return { promise, cancel: () => finish({ type: 'processing_failed' }) }
}
